import { BaseLogger } from "@app/config/loggers/baseLogger";
import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { AppLink } from "@base/ui/AppLink";
import { HorizontalScroll } from "@base/ui/HorizontalScroll";
import { Image } from "@base/ui/Image";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { ImageOff } from "lucide-react";
import { Suspense, useEffect } from "react";
import { projectsQueryOptions, useProjectsQuery } from "../model/useProjectsQuery";

const SKELETON_COUNT = 3;

function ProjectsCardSkeleton() {
  return (
    <HorizontalScroll className="gap-2 mt-4 md:mt-0" snap>
      {Array.from({ length: SKELETON_COUNT }, (_, i) => `skeleton-${i}`).map((key) => (
        <div key={key} className="w-[106px] shrink-0 animate-pulse md:w-[150px]">
          <div className="w-full aspect-[106/96] rounded-8 bg-gray-200 md:h-[110px]" />
          <div className="mt-2 mx-auto w-14 h-3 rounded-2 bg-gray-200 md:mx-0" />
        </div>
      ))}
    </HorizontalScroll>
  );
}

function ProjectsCardError() {
  return <p className="text-center text-body3 text-gray-500 py-10">Failed to load projects.</p>;
}

/**
 * 본문(ProjectsCardContent)과 같은 쿼리 키를 non-suspense `useQuery`로 구독해 캐시만 읽는다.
 * 던지거나 suspend하지 않으므로 Suspense/ErrorBoundary 없이 렌더해도 안전하고,
 * 최초 로딩 중에는 아무것도 그리지 않아 헤더 타이틀이 즉시 보인다.
 */
function ProjectsCardCount() {
  const { data } = useQuery(projectsQueryOptions());
  if (!data) return null;
  return <span className="hidden md:inline text-gray-500"> · {data.projects.length}</span>;
}

function ProjectsCardContent() {
  const { data } = useProjectsQuery();
  const projects = data.projects;

  // 사용자의 전체 현장 목록을 받는 곳은 여기뿐이다. 한 번 등록하면 프로필과
  // super property 로 남으므로, 이후 다른 화면의 이벤트도 이 축으로 나눌 수 있다.
  useEffect(
    function trackProjectMembership() {
      BaseLogger.setProjectMembership(projects.length);
    },
    [projects.length],
  );

  if (projects.length === 0) {
    return <div className="text-center text-body3 py-10">You haven't joined any projects yet.</div>;
  }

  return (
    <HorizontalScroll className="gap-2 mt-4 md:gap-4 md:mt-0" snap>
      {projects.map((project, index) => {
        const projectId = String(project.id);
        return (
          <AppLink
            to="/projects/$projectId"
            params={{ projectId }}
            key={projectId}
            // 홈에서 어디로 가는지가 없으면 진입 경로별 체류·완료율을 나눌 수 없다.
            // `position` 은 가로 스크롤에서 뒤쪽 카드가 실제로 눌리는지 보는 축이다.
            onClick={() =>
              EventLogger.click(CLIENT_EVENT.HOME_PROJECT_CLICK, { project_id: projectId, position: index })
            }
          >
            <figure className="w-[106px] shrink-0 md:w-[150px]">
              {project.thumbnail_image_url ? (
                <Image
                  src={project.thumbnail_image_url}
                  alt=""
                  // md 이상에서는 명시적 h-[110px]로 실제 박스 크기를 고정한다 — Image가 항상 붙이는
                  // 인라인 style="aspect-ratio:106/96"(CLS 방지용)는 height가 auto가 아니면 무시되므로
                  // md:aspect-[…] 클래스로 덮어쓰려 해도 인라인 스타일에 밀려 적용되지 않는다.
                  className="w-full object-cover aspect-[106/96] rounded-8 overflow-hidden md:h-[110px] md:border md:border-gray-200"
                  aspectRatio="106/96"
                />
              ) : (
                <div className="w-full aspect-[106/96] rounded-8 bg-gray-100 flex items-center justify-center md:h-[110px] md:border md:border-gray-200">
                  <ImageOff className="w-6 h-6 text-gray-300" />
                </div>
              )}

              <figcaption className="mt-2 text-center text-body4 md:text-left md:truncate">{project.name}</figcaption>
            </figure>
          </AppLink>
        );
      })}
    </HorizontalScroll>
  );
}

export function ProjectsCard() {
  return (
    <div className="p-4 bg-white rounded-8 shadow-box md:p-0 md:border md:border-gray-200 md:overflow-hidden">
      <div className="flex items-center md:justify-between md:px-4 md:py-3 md:border-b md:border-gray-200">
        <h2 className="text-subtitle4 text-gray-1000">
          Projects
          <ProjectsCardCount />
        </h2>
        <AppLink to="/projects" className="hidden md:inline text-body4 text-blue-600 hover:text-blue-700">
          View all
        </AppLink>
      </div>
      <div className="md:px-4 md:py-4">
        <ErrorBoundary fallback={<ProjectsCardError />}>
          <Suspense fallback={<ProjectsCardSkeleton />}>
            <ProjectsCardContent />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
}
